import Header from "../../Components/Header";
import SideBar from "../../Components/SideBar";
import { BallerzToken, BallerzRedirect } from "../../Client/Ballerz.js";
import { useGetRequest } from "../../Client/index.js";
import MobileDropdown from "../../Components/MobileDropdown";
import MobileFooter from "../../Components/MobileFooter";
import { useLocation } from "react-router-dom";
import AddFriend from "./add.js";
import { useEffect, useState } from "react";
import { sendPostRequest } from "../../Client/index.js";

export default function ManageFriends() {
  let ballerzToken = BallerzToken();

  const location = useLocation();
  const bookingReference = location.state?.bookingReference;

  if (!ballerzToken) {
    BallerzRedirect();
  }

  const { status, data } = useGetRequest("friends/list", {});

  const [friends, setFriends] = useState([]);

  const [removing, setRemoving] = useState({});
  const [accepting, setAccepting] = useState({});
  const [rejecting, setRejecting] = useState({});

  useEffect(() => {
    if (status === "fetched") {
      setFriends(Array.isArray(data?.users) ? data.users : []);
    }
  }, [status, data]);

  const getFriendKey = (f) =>
    f.otherUserID ?? f.ballerzUserID ?? f.username ?? String(f?.id ?? "");

  const count = friends.length;

  const handleRemoveFriend = async (friend) => {
    const id = getFriendKey(friend);
    if (!id) return;

    setRemoving((m) => ({ ...m, [id]: true }));
    try {
      const res = await sendPostRequest("friends/remove", { otherFriend: id });

      if (res?.status === "success") {
        setFriends((prev) => prev.filter((f) => getFriendKey(f) !== id));
      } else {
        throw new Error(res?.message || "Unable to remove friend");
      }
    } catch (e) {
      alert(e.message || "Failed to remove friend");
    } finally {
      setRemoving((m) => ({ ...m, [id]: false }));
    }
  };

  const handleAcceptFriend = async (friend) => {
    const id = getFriendKey(friend);
    if (!id) return;

    setAccepting((m) => ({ ...m, [id]: true }));
    try {
      const res = await sendPostRequest("friends/accept", { otherFriend: id });

      if (res?.status === "success") {
        setFriends((prev) =>
          prev.map((f) => (getFriendKey(f) === id ? { ...f, status: 1 } : f))
        );
      } else {
        throw new Error(res?.message || "Unable to accept friend");
      }
    } catch (e) {
      alert(e.message || "Failed to accept friend");
    } finally {
      setAccepting((m) => ({ ...m, [id]: false }));
    }
  };

  const handleRejectFriend = async (friend) => {
    const id = getFriendKey(friend);
    if (!id) return;

    setRejecting((m) => ({ ...m, [id]: true }));
    try {
      const res = await sendPostRequest("friends/reject", { otherFriend: id });
      if (res?.status === "success") {
        // actually remove it from the UI
        setFriends((prev) => prev.filter((f) => getFriendKey(f) !== id));
      } else {
        throw new Error(res?.message || "Unable to reject friend");
      }
    } catch (e) {
      alert(e.message || "Failed to reject friend");
    } finally {
      setRejecting((m) => ({ ...m, [id]: false }));
    }
  };

  const confirmRejectFriend = (friend) => {
    const label = friend.username || friend.name || "this user";
    if (
      window.confirm(
        `Are you sure you want to reject ${label}'s friend request?`
      )
    ) {
      handleRejectFriend(friend);
    }
  };

  if (status !== "fetched") return null;

  return (
    <div className="min-h-screen flex flex-col bg-[#070d27] text-white">
      <Header
        title={"skillzone"}
        users={friends}
        bookingReference={data?.bookingReference}
        username={data?.username}
      />

      <div className="flex flex-1">
        <SideBar usernames={data?.username} />

        <main className="flex-1 p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
            <div className="block sm:hidden">
              <MobileDropdown />
            </div>

            <div className="flex flex-col bg-[#1f2644] rounded-xl p-4 text-white Kallisto_Bold">
              <h2 className="text-2xl mb-4">
                {`${count} friend${count === 1 ? "" : "s"}`}
              </h2>

              <ul className="space-y-2">
                {count === 0 ? (
                  <li className="bg-[#2a3157] p-4 rounded text-center text-white/80">
                    No friends found
                  </li>
                ) : (
                  friends.map((friend, index) => {
                    const id = getFriendKey(friend);
                    const isRemoving = !!removing[id];
                    const isAccepting = !!accepting[id];
                    const isRejecting = !!rejecting[id];

                    return (
                      <li
                        key={id || index}
                        className="bg-[#2a3157] p-3 rounded flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2"
                      >
                        <span className="text-lg">{friend.username}</span>

                        {friend.status === 1 && (
                          <button
                            onClick={() => handleRemoveFriend(friend)}
                            disabled={isRemoving}
                            className="text-sm bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-4 py-2 rounded w-full sm:w-auto inline-flex items-center justify-center gap-2"
                          >
                            {isRemoving ? (
                              <>
                                <svg
                                  className="animate-spin h-4 w-4"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v4A4 4 0 008 12H4z"
                                  />
                                </svg>
                                Removing…
                              </>
                            ) : (
                              "Remove as Friend"
                            )}
                          </button>
                        )}

                        {friend.status === 0 &&
                          friend.otherField === "responder" && (
                            <span className="text-sm bg-[#4F55F6] text-white px-4 py-2 rounded w-full sm:w-auto text-center">
                              Request Sent
                            </span>
                          )}

                        {friend.status === 0 &&
                          friend.otherField === "requester" && (
                            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-end sm:items-center w-full">
                              <button
                                onClick={() => handleAcceptFriend(friend)}
                                disabled={isAccepting}
                                className="text-sm bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white px-6 py-2 rounded inline-flex items-center justify-center gap-2 w-full sm:w-auto sm:min-w-[120px]"
                              >
                                {isAccepting ? "Approving…" : "Approve"}
                              </button>

                              <button
                                onClick={() => confirmRejectFriend(friend)}
                                disabled={isRejecting}
                                className="text-sm bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-6 py-2 rounded inline-flex items-center justify-center gap-2 w-full sm:w-auto sm:min-w-[120px]"
                              >
                                {isRejecting ? "Rejecting…" : "Reject"}
                              </button>
                            </div>
                          )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>

            <AddFriend
              onAdded={(u) =>
                setFriends((prev) => {
                  const key = u.ballerzUserID ?? u.username;
                  const exists = prev.some(
                    (f) => (f.ballerzUserID ?? f.username) === key
                  );
                  if (exists) return prev;
                  return [
                    { ...u, status: 0, otherField: "responder" },
                    ...prev,
                  ];
                })
              }
            />
          </div>

          <div className="block sm:hidden mt-6">
            <MobileFooter />
          </div>
        </main>
      </div>
    </div>
  );
}
